import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { confirmar } from '../components/confirmacao';
import apiService from '../services/apiService';
import { useFasesDosVendedores, faseTexto } from '../components/FaseBadge';
import { BannerBody, truncate } from '../utils/bannerMarkdown';
import { criarEscala, empilharFaixas, marcasDoEixo } from '../components/reguaGeometria';
import './Comunicados.css';

/**
 * Comunicados — textos exibidos no app (role = admin).
 *
 * Três tipos, em abas, porque não se comportam igual:
 *   banner   -> aviso com janela de DATA E HORA. Fila: 1 aparece por vez.
 *   tip      -> dica com janela de DIA CHEIO (pode ser só 1 dia). Fila própria.
 *   behavior -> frase de comportamento: SEM janela e VÁRIAS por comportamento (repertório).
 *
 * A RÉGUA DE COMUNICAÇÃO mostra os 3 tipos, um bloco por tipo (a aba só filtra o formulário e a
 * lista abaixo). Cada tipo com janela tem sua própria lane "No app", que vem do backend
 * (/communications/timeline) calculada pela MESMA resolução do /communications/current que o app
 * chama — inclusive a granularidade por tipo — então mostra o que o vendedor vai receber. Frase de
 * comportamento aparece como barra do período inteiro, sem lane "No app": não há disputa, o app
 * recebe todas.
 *
 * A prévia do formulário é só do TEXTO FORMATADO (negrito/itálico/emoji). Não simula a UI do app de
 * propósito — o layout varia por tela e um mock daria uma falsa garantia.
 */

// Cor por escopo — ordem fixa (empresa→unidade→loja→vendedor), nunca ciclada.
// Paleta validada (banda de lightness, croma, separação CVD ΔE >= 13, contraste >= 3:1 no claro).
const SCOPE_COLOR = {
    company: '#1a5490',
    unit: '#00a08a',
    store: '#b8590a',
    salesman: '#7d3ac1',
    // Fase reusa o roxo do "placar travado" (.turmas-tag-trava / .fase-badge-trava): fase é a
    // mesma ideia em toda a interface, então tem a mesma cor. Escurecido para manter a separação
    // do roxo de vendedor na mesma faixa de lightness da paleta.
    phase: '#4a148c',
};
const SCOPE_LABEL = { company: 'Empresa', unit: 'Unidade', store: 'Loja', salesman: 'Vendedor', phase: 'Fase' };
// Ordem do menos para o MAIS específico — é a mesma do _SCOPE_RANK da API, e fase é a última.
const SCOPE_ORDER = ['company', 'unit', 'store', 'salesman', 'phase'];

const TYPES = ['banner', 'tip', 'behavior', 'phase_change', 'push'];
const TYPE_LABEL = {
    banner: 'Banners', tip: 'Dicas', behavior: 'Frases de comportamento',
    phase_change: 'Mudança de fase', push: 'Push',
};
const TYPE_ICON = { banner: '📣', tip: '💡', behavior: '🗣️', phase_change: '🚀', push: '🔔' };
const TYPE_SINGULAR = {
    banner: 'banner', tip: 'dica', behavior: 'frase de comportamento',
    phase_change: 'comunicado de mudança de fase', push: 'push',
};
const HAS_WINDOW = { banner: true, tip: true, behavior: false, phase_change: false, push: false };
// A régua desenha janelas de calendário, e o comunicado de mudança de fase não tem uma — a janela
// dele é a fase. Decisão do Caique (18/08): fora da régua, listado só na aba.
// O push (§N31) também fica fora: ele não fica "no ar" numa janela, é disparado em horários.
const TYPES_NA_REGUA = TYPES.filter((t) => t !== 'phase_change' && t !== 'push');

// §N31 — push. O texto é puro (o FCM não renderiza markdown) e os limites vêm do celular: o
// Android corta o título da notificação por volta de 65 caracteres.
const PUSH_MAX_TITLE = 65;
const PUSH_MAX_BODY = 500;
const WEEKDAYS = [[1, 'Seg'], [2, 'Ter'], [3, 'Qua'], [4, 'Qui'], [5, 'Sex'], [6, 'Sáb'], [7, 'Dom']];
const RESEND_LABEL = {
    once_per_occurrence: 'uma vez por ocorrência',
    every_slot: 'em todo horário, enquanto a regra valer',
    min_hours: 'no máximo a cada N horas',
};
// Fallback do registro de tags: a lista oficial vem do /push/rules (`tags`, objetos desde 24/09).
// Só é usado se a API ainda for a versão antiga, que devolvia só as duas tags de nome.
const PUSH_TAGS_FALLBACK = [
    { tag: '<first_name>', label: 'Primeiro nome', example: 'Maria', group: 'pessoa' },
    { tag: '<name>', label: 'Nome completo', example: 'Maria Souza', group: 'pessoa' },
];
const PUSH_TAG_GROUP_LABEL = { pessoa: 'Pessoa', loja: 'Loja', atividade: 'Atividade', desempenho: 'Desempenho' };
const PUSH_TAG_RE = /<([a-z_]+)(?:\|([^<>\n]*))?>/g;
/** Prévia: troca cada tag pelo exemplo do registro (ou pelo padrão escrito depois do `|`). */
const previewTags = (texto, tags) => (texto || '').replace(PUSH_TAG_RE, (m, nome, padrao) => {
    const t = tags.find((x) => x.tag === `<${nome}>`);
    return t ? t.example : (padrao ?? m);
});

/**
 * Horário em 24h com dois selects (hora 00–23, minuto de 5 em 5). O `<input type="time">` desenha
 * no formato do idioma do NAVEGADOR — com o Chrome em inglês aparece AM/PM, mesmo com a tela em
 * português —, e não há atributo que force 24h em todos. O valor continua "HH:MM".
 */
const HORAS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0'));
const MINUTOS = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));
function Horario24({ value, onChange }) {
    const [h, m] = (value || '10:00').split(':');
    // minuto fora do passo de 5 (gravado por outro caminho) continua aparecendo, em vez de sumir
    const mins = MINUTOS.includes(m) ? MINUTOS : [...MINUTOS, m].sort();
    return (
        <span className="cm-horario24">
            <select value={h} onChange={(e) => onChange(`${e.target.value}:${m}`)} aria-label="hora">
                {HORAS.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            <span>:</span>
            <select value={m} onChange={(e) => onChange(`${h}:${e.target.value}`)} aria-label="minuto">
                {mins.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
        </span>
    );
}

/** "Seg a Sáb", "Seg, Qua e Sex", "todo dia" — para o resumo em texto do push. */
const diasEmTexto = (dias) => {
    const ds = [...(dias || [])].sort();
    if (ds.length === 0 || ds.length === 7) return 'todo dia';
    const nome = (d) => WEEKDAYS.find(([n]) => n === d)[1];
    const seguidos = ds.every((d, i) => i === 0 || d === ds[i - 1] + 1);
    if (seguidos && ds.length >= 3) return `${nome(ds[0])} a ${nome(ds[ds.length - 1])}`;
    return ds.length === 1 ? nome(ds[0]) : `${ds.slice(0, -1).map(nome).join(', ')} e ${nome(ds[ds.length - 1])}`;
};

/** Resumo em linguagem natural de agenda + regra, o mesmo na lista e no formulário. */
const resumoPush = ({ schedule, rule, starts_at: inicio, rules }) => {
    let quando;
    if (!schedule) {
        quando = inicio ? `Uma vez, em ${fmtDateTime(inicio)}` : 'Uma vez, assim que publicado';
    } else {
        quando = `${diasEmTexto(schedule.weekdays)} às ${(schedule.times || []).join(', ')}`;
    }
    if (!rule) return `${quando}, para todos do escopo.`;
    const spec = (rules || []).find((r) => r.kind === rule.kind);
    const cond = rule.kind === 'no_recording'
        ? `quem está há ${rule.params?.days}+ dia${rule.params?.days === 1 ? '' : 's'} sem gravar`
        : `quem passa na regra "${spec?.label || rule.kind}"`;
    const rs = rule.resend || 'once_per_occurrence';
    const rep = typeof rs === 'object' ? `no máximo a cada ${rs.min_hours}h`
        : rs === 'every_slot' ? 'em todo horário' : 'uma vez por ocorrência';
    return `${quando}, envia para ${cond} (${rep}).`;
};
// As tags que o servidor substitui na entrega (§N27). Ficam listadas abaixo do texto: é o que
// evita a tag digitada errada, que hoje ninguém veria antes de o vendedor receber.
const TAGS_DINAMICAS = [
    ['<phase_name>', 'nome da fase em que ele entrou'],
    ['<previous_phase_name>', 'nome da fase que acabou'],
    ['<percent_of_valid_attendance>', '% dos atendimentos da turma que foram dele'],
    ['<best_behavior>', 'comportamento em que ele foi melhor'],
    ['<total_best_behavior>', 'pontos desse comportamento'],
    ['<worst_behavior>', 'comportamento a melhorar'],
    ['<total_worst_behavior>', 'pontos desse comportamento'],
];

const SEVERITY_LABEL = { info: 'Informativo', warning: 'Atenção', success: 'Positivo' };
const EMOJIS = ['😀', '😉', '🎉', '👏', '👍', '🙌', '💪', '🔥', '⭐', '🏆', '🎯', '📣', '📢', '📌',
    '📅', '⏰', '⚠️', '❗', '✅', '❌', '💡', '📈', '💰', '🛒', '🏬', '🚀', '❤️', '🙏'];

const MAX_BODY = 2000;

const pad = (n) => String(n).padStart(2, '0');
const toLocalInput = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const toDateInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const isoToLocalInput = (iso) => (iso ? toLocalInput(new Date(iso)) : '');
const isoToDateInput = (iso) => (iso ? toDateInput(new Date(iso)) : '');

const fmtDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};
const fmtDay = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;

// Data PURA (YYYY-MM-DD) formatada por string, sem passar por `new Date`. As datas de turma e de
// fase vêm sem hora, e `new Date('2026-09-01')` é lido como UTC — em Brasília isso mostra 31/08.
// O `fmtDate` acima continua valendo para os campos com hora (que vêm sem timezone e são locais).
const fmtDia = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');
// Mesma data como instante LOCAL, para a régua medir turma e comunicado na mesma escala.
const diaLocal = (iso, fimDoDia = false) => (iso ? `${iso}T${fimDoDia ? '23:59:59' : '00:00:00'}` : null);
/** Intersecção de intervalos com pontas abertas (null = sem limite). Fronteiras inclusivas. */
const cruza = (aDe, aAte, bDe, bAte) =>
    (aAte === null || bDe === null || aAte >= bDe) && (bAte === null || aDe === null || bAte >= aDe);

const addDays = (d, n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
};

const emptyForm = {
    id: null,
    type: 'banner',
    behavior_key: '',
    scope: 'company',
    unit_id: '',
    store_id: '',
    salesman_id: '',
    phase_id: '',
    title: '',
    icon: '',
    author: '',
    body: '',
    severity: 'info',
    cta_label: '',
    priority: 0,
    starts_at: '',
    ends_at: '',
    active: true,
    bump_version: false,
    // §N31 — push: agenda (quando checar) e regra (se envia)
    push_mode: 'once',                  // 'once' = envio único | 'recurring' = agenda
    push_times: ['10:00'],
    push_weekdays: [1, 2, 3, 4, 5, 6],
    push_tolerance: 60,
    push_collapse_key: '',              // '' = cada push vira uma notificação | chave = o novo substitui o anterior
    push_ttl: 60,                       // minutos que o FCM segura o push para um aparelho offline (com a chave)
    rule_kind: '',                      // '' = todos do escopo
    rule_params: {},
    rule_resend: 'once_per_occurrence', // ou 'every_slot' | 'min_hours'
    rule_min_hours: 24,
};

function Comunicados({ user, scope }) {
    const userId = user?.user_id;

    /**
     * ESCOPO EXTERNO (aba de Configurações) vs LOCAL (rota antiga).
     *
     * Quando esta tela é uma aba, quem manda é o `ScopeSelector` do shell: escolher a empresa duas
     * vezes na mesma página é justamente a redundância que o §9.1 existe para acabar. O estado local
     * continua no arquivo porque a rota antiga (`/comunicados`) ainda pode ser aberta direto — o dia
     * em que ela for removida, o `else` some junto.
     *
     * O escopo não é só a empresa: quando ele aponta para uma LOJA, a lista já vem filtrada por ela
     * e o select de loja desaparece (pinado, não escondido com valor divergente). Quando aponta para
     * uma FASE, a lista NÃO é filtrada — o CS precisa continuar vendo o que a empresa e a loja
     * mandam para aquele vendedor — mas o formulário de novo comunicado nasce naquela fase.
     */
    const escopoExterno = !!scope;
    const escopoLoja = escopoExterno && scope.kind === 'store';
    const escopoFase = escopoExterno && scope.kind === 'phase';
    const [toast, setToast] = useState(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    // --- contexto / filtros
    const [activeType, setActiveType] = useState('banner');
    const [companies, setCompanies] = useState([]);
    const [subdomainLocal, setSubdomainLocal] = useState('');
    const subdomain = escopoExterno ? (scope.subdomain || '') : subdomainLocal;
    const { fases: fasesVend } = useFasesDosVendedores({ subdomain });
    // Fases da empresa, para o escopo 'phase'. Vêm dos programas: uma lista plana
    // "Programa → N. Fase", a mesma forma do ScopeSelector, para o CS reconhecer.
    const [fasesDaEmpresa, setFasesDaEmpresa] = useState([]);
    useEffect(() => {
        if (!subdomain) { setFasesDaEmpresa([]); return; }
        let vivo = true;
        (async () => {
            try {
                const r = await apiService.phaseProgramsList({ subdomain });
                if (!vivo) return;
                const out = [];
                (r?.data?.programs || []).forEach((pr) => {
                    [...(pr.phases || [])]
                        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                        .forEach((f) => out.push({
                            id: f.id,
                            label: `${pr.name} → ${f.sort_order}. ${f.name}`,
                            locks_scoreboard: f.locks_scoreboard,
                        }));
                });
                setFasesDaEmpresa(out);
            } catch (e) { if (vivo) setFasesDaEmpresa([]); }
        })();
        return () => { vivo = false; };
    }, [subdomain]);
    // Trocar para uma empresa sem fase não pode deixar o formulário preso no escopo de fase: o
    // phase_id ficaria apontando para fase de OUTRA empresa, e a API recusaria com uma mensagem
    // que o CS não tem como relacionar ao que ele fez.
    useEffect(() => {
        if (!fasesDaEmpresa.length) {
            setForm((p) => (p.scope === 'phase' ? { ...p, scope: 'company', phase_id: '' } : p));
        }
    }, [fasesDaEmpresa]);

    const [stores, setStores] = useState([]);
    const [units, setUnits] = useState([]);
    const [behaviors, setBehaviors] = useState([]);
    // Loja do filtro: pinada pelo escopo quando ele é de loja, senão é escolha local.
    const [filterStoreLocal, setFilterStoreLocal] = useState('');
    const filterStoreId = escopoLoja ? String(scope.store_id || '') : filterStoreLocal;
    const [simSalesmanId, setSimSalesmanId] = useState('');
    const [simSalesmen, setSimSalesmen] = useState([]);
    const [rangeStart, setRangeStart] = useState(toDateInput(addDays(new Date(), -7)));
    const [rangeEnd, setRangeEnd] = useState(toDateInput(addDays(new Date(), 30)));

    // --- dados
    const [items, setItems] = useState([]);
    const [timeline, setTimeline] = useState(null);
    const [loading, setLoading] = useState(false);

    // --- formulário
    const [formOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [formSalesmen, setFormSalesmen] = useState([]);
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);   // guard síncrono contra double-submit
    const bodyRef = useRef(null);
    const [emojiOpen, setEmojiOpen] = useState(false);

    // --- detalhes
    const [hover, setHover] = useState(null);
    const [detailOf, setDetailOf] = useState(null);   // {id, rows}
    // §N31 — push: registro de regras (monta o formulário), prévia e histórico de envios
    const [pushRules, setPushRules] = useState([]);
    const [pushTags, setPushTags] = useState(PUSH_TAGS_FALLBACK);
    const [pushInfo, setPushInfo] = useState(null);   // {id, kind: 'preview'|'deliveries', data}
    useEffect(() => {
        if (activeType !== 'push' || pushRules.length) return;
        apiService.communicationsPushRules()
            .then((r) => {
                setPushRules(r.rules);
                // API nova devolve objetos; a antiga, strings — nesse caso fica o fallback
                if (Array.isArray(r.tags) && r.tags.length && typeof r.tags[0] === 'object') setPushTags(r.tags);
            })
            .catch(() => notify('Não foi possível carregar as regras de disparo', 'error'));
    }, [activeType, pushRules.length, notify]);

    const withWindow = HAS_WINDOW[activeType];

    const byId = useMemo(() => {
        const m = {};
        items.forEach((b) => { m[b.id] = b; });
        (timeline?.communications || []).forEach((b) => { if (!m[b.id]) m[b.id] = b; });
        return m;
    }, [items, timeline]);

    // Empresas — só quando a tela escolhe a empresa por conta própria (rota antiga). Como aba, a
    // lista já foi carregada pelo shell, e buscar de novo seria uma chamada a mais por abertura de
    // aba — além de poder disparar o toast de erro por algo que a tela nem usa.
    useEffect(() => {
        if (escopoExterno) return;
        (async () => {
            try {
                const data = await apiService.promptEditorGetCompanies();
                setCompanies(Array.isArray(data) ? data : []);
            } catch (e) {
                notify('Erro ao carregar empresas', 'error');
            }
        })();
    }, [notify, escopoExterno]);

    // Lojas ao trocar empresa
    useEffect(() => {
        setStores([]); setUnits([]); setBehaviors([]); setFilterStoreLocal(''); setSimSalesmanId('');
        setItems([]); setTimeline(null); setFormOpen(false);
        if (!subdomain) return;
        (async () => {
            try {
                const st = await apiService.promptEditorGetStores(subdomain);
                setStores(Array.isArray(st) ? st : []);
            } catch (e) { notify('Erro ao carregar lojas', 'error'); }
        })();
    }, [subdomain, notify]);

    // Vendedores para "simular como"
    useEffect(() => {
        setSimSalesmen([]); setSimSalesmanId('');
        if (!subdomain || !filterStoreId) return;
        (async () => {
            try {
                const r = await apiService.pointsSalesmen({ userId, subdomain, store_id: filterStoreId });
                setSimSalesmen(r.salesmen || []);
            } catch (e) { /* silencioso */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subdomain, filterStoreId, userId]);

    // Vendedores da loja escolhida NO FORMULÁRIO
    useEffect(() => {
        setFormSalesmen([]);
        if (!subdomain || !form.store_id) return;
        (async () => {
            try {
                const r = await apiService.pointsSalesmen({ userId, subdomain, store_id: form.store_id });
                setFormSalesmen(r.salesmen || []);
            } catch (e) { /* silencioso */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subdomain, form.store_id, userId]);

    const load = useCallback(async () => {
        if (!subdomain) return;
        setLoading(true);
        try {
            // a lista segue a aba; a RÉGUA mostra os 3 tipos, para dar a visão completa do período
            const [lst, tl] = await Promise.all([
                apiService.communicationsList({
                    subdomain, store_id: filterStoreId || undefined, types: [activeType],
                }),
                apiService.communicationsTimeline({
                    subdomain,
                    date_start: rangeStart,
                    date_end: rangeEnd,
                    store_id: filterStoreId || undefined,
                    salesman_id: simSalesmanId || undefined,
                    types: TYPES,
                    // as turmas do período: sem elas, escolher a janela do comunicado é chute (§9.13)
                    include_cohorts: true,
                }),
            ]);
            setItems(lst.communications || []);
            setUnits(lst.units || []);
            setBehaviors(lst.behaviors || []);
            setTimeline(tl || null);
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao carregar comunicados', 'error');
        } finally {
            setLoading(false);
        }
    }, [subdomain, activeType, filterStoreId, simSalesmanId, rangeStart, rangeEnd, notify]);

    useEffect(() => { load(); }, [load]);

    // ------------------------------------------------------------------ form
    const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

    const openNew = () => {
        const now = new Date();
        now.setMinutes(0, 0, 0);
        setForm({
            ...emptyForm,
            type: activeType,
            // O novo comunicado nasce NO ESCOPO ESCOLHIDO: quem entrou em "Onboarding → 3.
            // Comportamentos" e clicou em novo quer um comunicado daquela fase, não da empresa.
            scope: escopoFase ? 'phase' : (filterStoreId ? 'store' : 'company'),
            phase_id: escopoFase ? String(scope.phase_id) : '',
            store_id: filterStoreId || '',
            starts_at: (activeType === 'behavior' || activeType === 'phase_change' || activeType === 'push') ? ''
                : activeType === 'tip' ? toDateInput(now) : toLocalInput(now),
            ends_at: activeType === 'tip' ? toDateInput(now) : '',
        });
        setFormOpen(true);
    };

    const openEdit = (b) => {
        const dayOnly = b.type === 'tip';
        setForm({
            id: b.id,
            type: b.type,
            behavior_key: b.behavior_key || '',
            scope: b.scope,
            unit_id: b.unit_id ? String(b.unit_id) : '',
            store_id: b.store_id ? String(b.store_id) : '',
            salesman_id: b.salesman_id ? String(b.salesman_id) : '',
            phase_id: b.phase_id ? String(b.phase_id) : '',
            title: b.title || '',
            icon: b.icon || '',
            author: b.author || '',
            body: b.body || '',
            severity: b.severity || 'info',
            cta_label: b.cta_label || '',
            priority: b.priority ?? 0,
            starts_at: dayOnly ? isoToDateInput(b.starts_at) : isoToLocalInput(b.starts_at),
            ends_at: dayOnly ? isoToDateInput(b.ends_at) : isoToLocalInput(b.ends_at),
            active: !!b.active,
            bump_version: false,
            ...pushFormFields(b),
        });
        setFormOpen(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /** agenda + regra gravadas -> campos do formulário (o inverso de `pushPayload`) */
    const pushFormFields = (b) => {
        if (b.type !== 'push') return {};
        const sch = b.delivery_schedule;
        const rule = b.trigger_rule;
        const rs = rule?.resend || 'once_per_occurrence';
        return {
            push_mode: sch?.times?.length ? 'recurring' : 'once',
            push_times: sch?.times?.length ? sch.times : emptyForm.push_times,
            push_weekdays: sch?.weekdays || (sch ? [1, 2, 3, 4, 5, 6, 7] : emptyForm.push_weekdays),
            push_tolerance: sch?.late_tolerance_minutes ?? 60,
            push_collapse_key: sch?.collapse_key || '',
            push_ttl: sch?.ttl_minutes ?? 60,
            rule_kind: rule?.kind || '',
            rule_params: rule?.params || {},
            rule_resend: typeof rs === 'object' ? 'min_hours' : rs,
            rule_min_hours: typeof rs === 'object' ? rs.min_hours : 24,
        };
    };

    /** campos do formulário -> {delivery_schedule, trigger_rule} que o /save espera */
    const pushPayload = (f) => {
        // `collapse_key` e `ttl_minutes` são opções de ENTREGA: valem também no envio único e no push
        // dirigido por regra/evento, que não têm horários (a API aceita a agenda só com as opções)
        const collapse = f.push_collapse_key.trim()
            ? { collapse_key: f.push_collapse_key.trim(), ttl_minutes: Number(f.push_ttl) || 60 } : {};
        const delivery_schedule = f.push_mode === 'recurring' ? {
            times: f.push_times.filter(Boolean),
            weekdays: f.push_weekdays,
            late_tolerance_minutes: Number(f.push_tolerance) || 60,
            ...collapse,
        } : (Object.keys(collapse).length ? collapse : null);
        let trigger_rule = null;
        if (f.rule_kind) {
            const spec = pushRules.find((r) => r.kind === f.rule_kind);
            const params = {};
            (spec?.params || []).forEach((p) => {
                const v = f.rule_params[p.name] ?? p.default;
                if (v !== '' && v !== undefined && v !== null) params[p.name] = p.type === 'integer' ? Number(v) : v;
            });
            trigger_rule = {
                kind: f.rule_kind,
                params,
                resend: f.rule_resend === 'min_hours' ? { min_hours: Number(f.rule_min_hours) || 24 } : f.rule_resend,
            };
        }
        return { delivery_schedule, trigger_rule };
    };

    const duplicate = (b) => {
        openEdit(b);
        setForm((prev) => ({ ...prev, id: null, bump_version: false }));
    };

    const closeForm = () => { setFormOpen(false); setForm(emptyForm); setEmojiOpen(false); };

    /** Só hoje: preenche início e fim com o dia corrente (a dica vale o dia cheio). */
    const setTipToday = () => {
        const today = toDateInput(new Date());
        setForm((p) => ({ ...p, starts_at: today, ends_at: today }));
    };

    /** Envolve a seleção do textarea com um marcador (negrito/itálico). */
    const applyMark = (before, after = before) => {
        const el = bodyRef.current;
        if (!el) return;
        const { selectionStart: s, selectionEnd: e, value } = el;
        const sel = value.slice(s, e) || 'texto';
        const next = value.slice(0, s) + before + sel + after + value.slice(e);
        setField('body', next.slice(0, MAX_BODY));
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(s + before.length, s + before.length + sel.length);
        });
    };

    const insertText = (txt) => {
        const el = bodyRef.current;
        if (!el) { setField('body', (form.body + txt).slice(0, MAX_BODY)); return; }
        const { selectionStart: s, selectionEnd: e, value } = el;
        const next = (value.slice(0, s) + txt + value.slice(e)).slice(0, MAX_BODY);
        setField('body', next);
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(s + txt.length, s + txt.length);
        });
    };

    const targetOk =
        (form.scope === 'company') ||
        (form.scope === 'unit' && form.unit_id) ||
        (form.scope === 'store' && form.store_id) ||
        (form.scope === 'salesman' && form.salesman_id) ||
        (form.scope === 'phase' && form.phase_id);
    // `phase_change` não tem data nem comportamento: exigir `starts_at` dele deixaria o botão
    // Salvar desabilitado para sempre, sem dizer por quê.
    const typeOk = form.type === 'behavior' ? !!form.behavior_key
        : form.type === 'phase_change' ? true
            : form.type === 'push'
                // push: título obrigatório (é a linha que aparece na notificação) e, se
                // recorrente, pelo menos um horário. Data é opcional (vazio = já).
                ? (!!form.title.trim() && form.title.length <= PUSH_MAX_TITLE
                    && form.body.length <= PUSH_MAX_BODY
                    && (form.push_mode === 'once' || form.push_times.some(Boolean)))
                : !!form.starts_at;
    const canSave = !!form.body.trim() && targetOk && typeOk;

    const save = async () => {
        if (!canSave) {
            notify(form.type === 'behavior'
                ? 'Preencha o comportamento, o texto e o alvo'
                : form.type === 'phase_change'
                    ? 'Escolha a FASE no seletor acima e escreva o texto'
                    : form.type === 'push'
                        ? 'Preencha título, texto, alvo e pelo menos um horário'
                        : 'Preencha o texto, a data de início e o alvo', 'error');
            return;
        }
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        try {
            await apiService.communicationsSave({
                id: form.id || undefined,
                subdomain,
                type: form.type,
                behavior_key: form.type === 'behavior' ? form.behavior_key : null,
                scope: form.scope,
                unit_id: form.scope === 'unit' ? Number(form.unit_id) : null,
                store_id: form.scope === 'store' ? Number(form.store_id) : null,
                salesman_id: form.scope === 'salesman' ? Number(form.salesman_id) : null,
                phase_id: form.scope === 'phase' ? Number(form.phase_id) : null,
                title: form.title.trim() || null,
                icon: form.icon.trim() || null,
                // autor da FRASE (a citação), não quem cadastrou — só faz sentido em comportamento
                author: form.type === 'behavior' ? (form.author.trim() || null) : null,
                body: form.body,
                severity: form.severity,
                cta_label: form.cta_label.trim() || null,
                priority: Number(form.priority) || 0,
                starts_at: (form.type === 'behavior' || form.type === 'phase_change' || form.type === 'push')
                    ? (form.starts_at || null) : form.starts_at,
                ends_at: form.type === 'phase_change' ? null
                    // envio único não tem "até": o fim só limita a agenda recorrente
                    : (form.type === 'push' && form.push_mode === 'once') ? null
                        : (form.ends_at || null),
                active: form.active,
                bump_version: !!form.bump_version,
                ...(form.type === 'push' ? pushPayload(form) : {}),
            });
            notify(form.id ? 'Comunicado atualizado' : 'Comunicado criado', 'success');
            closeForm();
            load();
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao salvar comunicado', 'error');
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    const remove = async (b) => {
        if (!(await confirmar(`Remover agora ${TYPE_SINGULAR[b.type]} "${truncate(b.body, 40)}" (${b.target_label})?\n\nSai do app no próximo ciclo. O histórico de interações é preservado.`, { titulo: 'Remover comunicado', perigo: true, rotuloConfirmar: 'Remover' }))) return;
        try {
            await apiService.communicationsDelete({ subdomain, id: b.id });
            notify('Comunicado removido', 'success');
            load();
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao remover comunicado', 'error');
        }
    };

    /** Liga/desliga a exibição em um clique (não passa pelo formulário, não mexe na versão). */
    const toggleActive = async (b) => {
        try {
            await apiService.communicationsToggleActive({ subdomain, id: b.id, active: !b.active });
            notify(b.active ? 'Despublicado' : 'Publicado', 'success');
            load();
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao alterar a publicação', 'error');
        }
    };

    /** Prévia ("quem receberia agora") e histórico de envios do push — abrem na linha dele. */
    const showPush = async (b, kind) => {
        if (pushInfo?.id === b.id && pushInfo.kind === kind) { setPushInfo(null); return; }
        try {
            const data = kind === 'preview'
                ? await apiService.communicationsPushPreview({ subdomain, id: b.id })
                : await apiService.communicationsPushDeliveries({ subdomain, id: b.id });
            setPushInfo({ id: b.id, kind, data });
        } catch (e) {
            notify(e.response?.data?.detail || e.message || 'Erro ao carregar', 'error');
        }
    };

    const showDetail = async (b) => {
        if (detailOf?.id === b.id) { setDetailOf(null); return; }
        try {
            const r = await apiService.communicationsInteractions({ subdomain, id: b.id });
            setDetailOf({ id: b.id, rows: r.interactions || [] });
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao carregar interações', 'error');
        }
    };

    // -------------------------------------------------------------- régua
    // Um bloco por tipo: banner e dica têm filas independentes (cada um com sua linha "No app") e a
    // frase de comportamento não tem fila nem janela — entra como barra do período inteiro.
    const regua = useMemo(() => {
        if (!timeline) return null;
        // Escala e empilhamento vêm de `reguaGeometria`, compartilhado com a régua de fases por
        // loja (§9.10). A matemática era daqui e foi extraída sem mudar um número — duas réguas com
        // cálculo próprio divergiriam na primeira correção de borda que só uma recebesse.
        const escala = criarEscala(timeline.range.from, timeline.range.to);
        const { t0, t1 } = escala;
        const pct = (iso, fallback) => escala.pct(iso, fallback);
        const packRows = empilharFaixas;

        const all = timeline.communications || [];
        const blocks = TYPES_NA_REGUA.map((type) => {
            const ofType = all.filter((b) => b.type === type);
            return {
                type,
                total: ofType.length,
                lanes: SCOPE_ORDER.map((scope) => ({
                    scope,
                    rows: packRows(ofType.filter((b) => b.scope === scope).map((b) => ({
                        ...b,
                        left: pct(b.starts_at, t0),
                        right: pct(b.ends_at, t1),
                        alwaysOn: !b.starts_at && !b.ends_at,
                        neverShown: (timeline.never_shown || []).includes(b.id),
                    }))),
                })).filter((l) => l.rows.length > 0),
                segs: (timeline.segments?.[type] || []).map((s) => ({
                    ...s,
                    left: pct(s.from, t0),
                    right: pct(s.to, t1),
                })),
            };
        }).filter((b) => b.total > 0);

        // TURMAS: o calendário de quem pode receber. `date_end` da API é o ÚLTIMO dia DENTRO, então a
        // barra vai até 23:59 dele — terminar às 00:00 desenharia a turma acabando um dia antes.
        const faixaDe = (o) => ({
            ...o,
            left: pct(diaLocal(o.date_start), t0),
            right: pct(diaLocal(o.date_end, true), t1),
        });
        const turmas = empilharFaixas((timeline.cohorts || []).map((c) => ({
            ...faixaDe(c),
            fases: (c.phases || []).map(faixaDe),
        })));

        const { marcas: ticks } = marcasDoEixo(escala, fmtDay);
        const nowLeft = pct(new Date().toISOString(), t0);
        const nowVisible = Date.now() >= t0 && Date.now() <= t1;

        return { blocks, ticks, nowLeft, nowVisible, turmas };
    }, [timeline]);

    // ------------------------------------------------------- quem recebe, em TURMAS
    // A pergunta que a régua não respondia: "quem vai ver esta dica?". Turma é a unidade em que o CS
    // pensa (§9.13). O cálculo é aqui, no front, porque muda a cada tecla do formulário — a API
    // devolve os FATOS (janelas materializadas, lojas dos membros, vendedores) e a tela cruza.
    const turmasNaRegua = useMemo(() => timeline?.cohorts || [], [timeline]);

    const turmasQueVeem = useCallback((alvo) => {
        if (!alvo) return [];
        const dia = (v) => (v ? String(v).slice(0, 10) : null);
        const de = dia(alvo.starts_at);
        const ate = dia(alvo.ends_at);
        const lojasDaUnidade = String(alvo.scope) === 'unit'
            ? (units.find((u) => String(u.id) === String(alvo.unit_id))?.store_ids || []).map(Number)
            : [];
        return (timeline?.cohorts || []).map((c) => {
            if (!c.members) return null;          // turma sem membro não recebe nada
            // No escopo de FASE a janela que importa é a da fase dentro da turma: a turma pode durar
            // seis meses e a fase quinze dias. Comparar com a janela da turma prometeria entrega.
            const faixa = alvo.scope === 'phase'
                ? (c.phases || []).find((f) => String(f.phase_id) === String(alvo.phase_id))
                : c;
            if (!faixa) return null;
            if (!cruza(de, ate, faixa.date_start, faixa.date_end)) return null;
            // Alcance por alvo. Loja e unidade olham a loja ATUAL dos membros (`member_store_ids`),
            // não a loja de origem da turma: depois de uma troca de loja (§9.8) quem manda é onde o
            // vendedor está hoje, que é o que a subconsulta de audiência da API também usa.
            const alcanca = alvo.scope === 'company' || alvo.scope === 'phase' ? true
                : alvo.scope === 'unit' ? (c.member_store_ids || []).some((x) => lojasDaUnidade.includes(Number(x)))
                    : alvo.scope === 'store' ? (c.member_store_ids || []).some((x) => Number(x) === Number(alvo.store_id))
                        : alvo.scope === 'salesman' ? (c.salesman_ids || []).some((x) => Number(x) === Number(alvo.salesman_id))
                            : false;
            return alcanca ? { ...c, faixa } : null;
        }).filter(Boolean);
    }, [timeline, units]);

    // Turmas alcançadas pelo que está sendo escrito (ou pelo comunicado sob o mouse, quando o
    // formulário está fechado) — é o que acende as barras na faixa de turmas.
    const audiencia = useMemo(
        () => (formOpen ? turmasQueVeem(form) : []),
        [formOpen, form, turmasQueVeem],
    );
    const audienciaHover = useMemo(
        () => (!formOpen && hover?.item ? turmasQueVeem(hover.item) : []),
        [formOpen, hover, turmasQueVeem],
    );
    const turmasAcesas = useMemo(() => {
        const fonte = formOpen ? audiencia : audienciaHover;
        return { ativo: formOpen || !!hover?.item, ids: new Set(fonte.map((c) => c.cohort_id)) };
    }, [formOpen, hover, audiencia, audienciaHover]);

    // Onde a fase escolhida acontece, IGNORANDO a janela do comunicado: é o que permite dizer ao CS
    // as datas certas quando ele marcou um período que não cruza a fase.
    const faseNasTurmas = useMemo(() => {
        if (form.scope !== 'phase' || !form.phase_id) return [];
        return turmasNaRegua.flatMap((c) => (c.phases || [])
            .filter((f) => String(f.phase_id) === String(form.phase_id) && c.members)
            .map((f) => ({ cohort: c.name, store: c.store_name, ...f })));
    }, [form.scope, form.phase_id, turmasNaRegua]);

    const simLabel = simSalesmanId
        ? simSalesmen.find((s) => String(s.salesman_id) === String(simSalesmanId))?.salesman_name
        : filterStoreId
            ? stores.find((s) => String(s.id) === String(filterStoreId))?.display_name
            : null;

    // frases agrupadas por comportamento (só na aba behavior)
    const grouped = useMemo(() => {
        if (withWindow) return null;
        const g = {};
        items.forEach((b) => {
            const k = b.behavior_name || b.behavior_key || '—';
            (g[k] = g[k] || []).push(b);
        });
        return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
    }, [items, withWindow]);

    const interactionChips = (b) => (
        <button className="cm-chips" onClick={() => showDetail(b)} title="Ver quem leu, confirmou e reagiu">
            <span title="leram">👁 {b.read_count}</span>
            <span title="confirmaram">✓ {b.dismissed_count}</span>
            <span title="gostaram">👍 {b.like_count}</span>
            <span title="não gostaram">👎 {b.dislike_count}</span>
            <small>de {b.audience_count ?? '—'}</small>
        </button>
    );

    const detailRow = (b, colSpan) => detailOf?.id === b.id && (
        <tr className="cm-detail-row">
            <td colSpan={colSpan}>
                <div className="cm-readers">
                    <div className="cm-readers-head">
                        <strong>Interações ({detailOf.rows.length})</strong>
                        <button className="cm-btn cm-btn-sm" onClick={() => setDetailOf(null)}>Fechar</button>
                    </div>
                    {detailOf.rows.length === 0 && <p className="cm-empty">Ninguém interagiu ainda.</p>}
                    {detailOf.rows.length > 0 && (
                        <table className="cm-table cm-table-inner">
                            <thead>
                                <tr>
                                    <th>Vendedor</th><th>Loja</th><th>Versão</th>
                                    <th>Leu</th><th>Confirmou</th><th>Reação</th><th>Aparelho logado como</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detailOf.rows.map((r, i) => (
                                    <tr key={i}>
                                        <td>{r.name || `user ${r.user_id}`}</td>
                                        <td>{r.store_name || '—'}</td>
                                        <td>v{r.version}</td>
                                        <td>{fmtDateTime(r.read_at)}</td>
                                        <td>{fmtDateTime(r.dismissed_at)}</td>
                                        <td>
                                            {r.reaction === 1 ? '👍' : r.reaction === -1 ? '👎' : '—'}
                                            {r.reaction_at && <small> {fmtDateTime(r.reaction_at)}</small>}
                                        </td>
                                        <td>{r.acted_by_name || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </td>
        </tr>
    );

    return (
        <div className="cm-page">
            {toast && <div className={`cm-toast cm-toast-${toast.type}`}>{toast.msg}</div>}

            <h1 className="cm-title">📣 Comunicados</h1>

            <div className="cm-tabs">
                {TYPES.map((t) => (
                    <button
                        key={t}
                        className={`cm-tab${activeType === t ? ' active' : ''}`}
                        onClick={() => { setActiveType(t); setFormOpen(false); setDetailOf(null); setPushInfo(null); }}
                    >
                        {TYPE_ICON[t]} {TYPE_LABEL[t]}
                    </button>
                ))}
            </div>

            <div className="cm-filters">
                {!escopoExterno && (
                    <div className="cm-field">
                        <label>Empresa</label>
                        <select value={subdomainLocal} onChange={(e) => setSubdomainLocal(e.target.value)}>
                            <option value="">— Selecione —</option>
                            {companies.map((c) => <option key={c.subdomain} value={c.subdomain}>{c.name}</option>)}
                        </select>
                    </div>
                )}
                {escopoLoja ? (
                    <div className="cm-field">
                        <label>Loja</label>
                        <div className="cm-pinned" title="Definido pelo escopo escolhido acima">
                            {stores.find((s) => String(s.id) === String(scope.store_id))?.display_name
                             || stores.find((s) => String(s.id) === String(scope.store_id))?.name
                             || `Loja ${scope.store_id}`}
                        </div>
                    </div>
                ) : (
                    <div className="cm-field">
                        <label>Loja</label>
                        <select value={filterStoreId} onChange={(e) => setFilterStoreLocal(e.target.value)} disabled={!subdomain}>
                            <option value="">Todas</option>
                            {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                        </select>
                    </div>
                )}
                {withWindow && (
                    <>
                        <div className="cm-field">
                            <label>Simular como (vendedor)</label>
                            <select value={simSalesmanId} onChange={(e) => setSimSalesmanId(e.target.value)} disabled={!filterStoreId}>
                                <option value="">— Toda a loja —</option>
                                {simSalesmen.map((s) => (
                                    <option key={s.salesman_id} value={s.salesman_id}>
                                        {s.salesman_name}{s.is_active ? '' : ' (inativo)'}{faseTexto(fasesVend[String(s.salesman_id)], { comDia: false })}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="cm-field">
                            <label>Régua de</label>
                            <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} />
                        </div>
                        <div className="cm-field">
                            <label>até</label>
                            <input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} />
                        </div>
                    </>
                )}
                <div className="cm-field cm-filters-actions">
                    <button className="cm-btn cm-btn-primary" onClick={openNew} disabled={!subdomain}>
                        ➕ {activeType === 'behavior' ? 'Nova frase'
                            : activeType === 'tip' ? 'Nova dica'
                                : activeType === 'phase_change' ? 'Novo comunicado de fase'
                                    : activeType === 'push' ? 'Novo push'
                                        : 'Novo banner'}
                    </button>
                    <button className="cm-btn" onClick={load} disabled={!subdomain || loading}>
                        {loading ? 'Carregando…' : '↻ Atualizar'}
                    </button>
                </div>
            </div>

            {!subdomain && <p className="cm-empty">Selecione uma empresa para ver os comunicados.</p>}

            {/* -------------------------------------------------------- régua */}
            {subdomain && regua && (
                <div className="cm-regua">
                    <div className="cm-regua-head">
                        <h2>Régua de comunicação</h2>
                        <div className="cm-regua-meta">
                            {timeline.overlap_count > 0 && (
                                <span className="cm-badge cm-badge-warn">
                                    ⚠ {timeline.overlap_count} {timeline.overlap_count === 1 ? 'intervalo com sobreposição' : 'intervalos com sobreposição'}
                                </span>
                            )}
                            {timeline.never_shown?.length > 0 && (
                                <span className="cm-badge cm-badge-danger">
                                    ⚠ {timeline.never_shown.length} nunca {timeline.never_shown.length === 1 ? 'aparece' : 'aparecem'} no período
                                </span>
                            )}
                            <span className="cm-regua-sim">
                                {simLabel ? `Simulando: ${simLabel}` : 'Sem alvo simulado — só o que atinge toda a empresa'}
                            </span>
                        </div>
                    </div>

                    <div className="cm-legend">
                        {SCOPE_ORDER.map((s) => (
                            <span key={s} className="cm-legend-item">
                                <i style={{ background: SCOPE_COLOR[s] }} /> {SCOPE_LABEL[s]}
                            </span>
                        ))}
                        <span className="cm-legend-item cm-legend-hint">
                            <i className="cm-swatch-hatch" /> coberto por outro (nunca aparece)
                        </span>
                    </div>

                    <div className="cm-chart">
                        <div className="cm-axis">
                            <span className="cm-lane-label" />
                            <div className="cm-axis-track">
                                {regua.ticks.map((t, i) => (
                                    <span key={i} className="cm-tick" style={{ left: `${t.left}%` }}>{t.label}</span>
                                ))}
                            </div>
                        </div>

                        {/* TURMAS — o calendário de quem pode receber, logo abaixo do eixo para ser lido
                            junto com as barras dos comunicados. As faixas de fase vêm dentro da barra
                            da turma: é nelas que um comunicado de fase pega. */}
                        {regua.turmas.length > 0 && (
                            <div className="cm-block cm-turmas">
                                <div className="cm-block-head">
                                    <span className="cm-block-title cm-block-title-static">
                                        🎓 Turmas <small>({turmasNaRegua.length})</small>
                                    </span>
                                    <span className="cm-block-note">
                                        {turmasAcesas.ativo
                                            ? `${turmasAcesas.ids.size} de ${turmasNaRegua.length} ${turmasAcesas.ids.size === 1 ? 'recebe' : 'recebem'}`
                                            : 'período em que cada turma existe — e as fases dentro dela'}
                                    </span>
                                </div>
                                {regua.turmas.map((row, ri) => (
                                    <div key={ri} className="cm-lane cm-lane-turma">
                                        <span className="cm-lane-label">{ri === 0 ? 'Turmas' : ''}</span>
                                        <div className="cm-lane-track">
                                            {regua.nowVisible && <span className="cm-now" style={{ left: `${regua.nowLeft}%` }} />}
                                            {row.map((c) => (
                                                <div
                                                    key={c.cohort_id}
                                                    className={`cm-turma${turmasAcesas.ativo && !turmasAcesas.ids.has(c.cohort_id) ? ' cm-turma-fora' : ''}${turmasAcesas.ids.has(c.cohort_id) ? ' cm-turma-alvo' : ''}`}
                                                    style={{ left: `${c.left}%`, width: `${Math.max(1.2, c.right - c.left)}%` }}
                                                    title={`${c.name} — ${c.store_name || 'sem loja'}\n${c.program_name}\n${fmtDia(c.date_start)} → ${c.date_end ? fmtDia(c.date_end) : 'sem fim previsto'}\n${c.members} ${c.members === 1 ? 'membro' : 'membros'}${c.members === 0 ? ' (ninguém recebe)' : ''}`}
                                                >
                                                    {/* fases dentro da turma, posicionadas na MESMA escala: o percentual de
                                                        cada fase é do gráfico inteiro, então converto para dentro da barra */}
                                                    {(c.fases || []).map((f) => {
                                                        const larguraTurma = Math.max(0.001, c.right - c.left);
                                                        return (
                                                            <span
                                                                key={f.phase_id}
                                                                className={`cm-fase${f.locks_scoreboard ? ' cm-fase-trava' : ''}`}
                                                                style={{
                                                                    left: `${((f.left - c.left) / larguraTurma) * 100}%`,
                                                                    width: `${Math.max(0.5, ((f.right - f.left) / larguraTurma) * 100)}%`,
                                                                }}
                                                                title={`${f.name}: ${fmtDia(f.date_start)} → ${f.date_end ? fmtDia(f.date_end) : 'sem fim'}\n${f.members} ${f.members === 1 ? 'membro passa' : 'membros passam'} por ela${f.locks_scoreboard ? '\nplacar fechado nesta fase' : ''}`}
                                                            />
                                                        );
                                                    })}
                                                    <span className="cm-turma-text">
                                                        {c.name}{c.members === 0 ? ' (vazia)' : ''}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {regua.blocks.length === 0 && <p className="cm-empty">Nada cadastrado no período.</p>}

                        {regua.blocks.map((block) => (
                            <div
                                key={block.type}
                                className={`cm-block${block.type === activeType ? ' cm-block-active' : ''}`}
                            >
                                <div className="cm-block-head">
                                    <button className="cm-block-title" onClick={() => setActiveType(block.type)}>
                                        {TYPE_ICON[block.type]} {TYPE_LABEL[block.type]} <small>({block.total})</small>
                                    </button>
                                    {!HAS_WINDOW[block.type] && (
                                        <span className="cm-block-note">sem período — vale sempre, e o app recebe todas</span>
                                    )}
                                </div>

                                {block.lanes.map((lane) => (
                                    <div key={lane.scope} className="cm-lane">
                                        <span className="cm-lane-label">{SCOPE_LABEL[lane.scope]}</span>
                                        {/* altura cresce com o nº de faixas: janelas sobrepostas não se escondem */}
                                        <div className="cm-lane-track" style={{ height: lane.rows.length * 24 + 4 }}>
                                            {regua.nowVisible && <span className="cm-now" style={{ left: `${regua.nowLeft}%` }} />}
                                            {lane.rows.map((row, ri) => row.map((b) => (
                                                <button
                                                    key={b.id}
                                                    type="button"
                                                    className={`cm-bar${b.neverShown ? ' cm-bar-covered' : ''}${b.status !== 'ativo' ? ' cm-bar-dim' : ''}`}
                                                    style={{
                                                        left: `${b.left}%`,
                                                        width: `${Math.max(1.2, b.right - b.left)}%`,
                                                        top: ri * 24 + 3,
                                                        background: SCOPE_COLOR[b.scope],
                                                    }}
                                                    onMouseEnter={(e) => setHover({ item: b, x: e.clientX, y: e.clientY })}
                                                    onMouseMove={(e) => setHover({ item: b, x: e.clientX, y: e.clientY })}
                                                    onMouseLeave={() => setHover(null)}
                                                    onClick={() => openEdit(byId[b.id] || b)}
                                                >
                                                    <span className="cm-bar-text">
                                                        {b.neverShown ? '⚠ ' : ''}{b.status === 'inativo' ? '⏸ ' : ''}
                                                        {b.behavior_name ? `${b.behavior_name}: ` : ''}
                                                        {b.title || truncate(b.body, 40)}
                                                    </span>
                                                </button>
                                            )))}
                                        </div>
                                    </div>
                                ))}

                                {HAS_WINDOW[block.type] && (
                                    <div className="cm-lane cm-lane-effective">
                                        <span className="cm-lane-label">No app</span>
                                        <div className="cm-lane-track">
                                            {regua.nowVisible && <span className="cm-now" style={{ left: `${regua.nowLeft}%` }} />}
                                            {block.segs.length === 0 && <span className="cm-lane-empty">nada será exibido no período</span>}
                                            {block.segs.map((s, i) => {
                                                const b = byId[s.communication_id]
                                                    || block.lanes.flatMap((l) => l.rows.flat()).find((x) => x.id === s.communication_id);
                                                return (
                                                    <button
                                                        key={i}
                                                        type="button"
                                                        className={`cm-seg${s.overlap ? ' cm-seg-overlap' : ''}`}
                                                        style={{
                                                            left: `${s.left}%`,
                                                            width: `${Math.max(1.2, s.right - s.left)}%`,
                                                            background: b ? SCOPE_COLOR[b.scope] : '#5a6b78',
                                                        }}
                                                        onMouseEnter={(e) => b && setHover({ item: b, x: e.clientX, y: e.clientY, seg: s })}
                                                        onMouseMove={(e) => b && setHover({ item: b, x: e.clientX, y: e.clientY, seg: s })}
                                                        onMouseLeave={() => setHover(null)}
                                                        onClick={() => b && openEdit(b)}
                                                    >
                                                        <span className="cm-bar-text">
                                                            {b ? (b.title || truncate(b.body, 40)) : `#${s.communication_id}`}
                                                            {s.overlap ? ` (vence ${s.eligible_ids.length})` : ''}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <p className="cm-regua-foot">
                        Cada tipo tem sua própria linha <strong>No app</strong>, calculada pelo mesmo endpoint que o
                        app consome: em cada intervalo mostra o texto que o vendedor realmente vai receber.
                        <strong> Banner e dica não competem entre si</strong> — as filas são separadas. Empate dentro
                        do mesmo tipo resolve por <em>prioridade</em>, depois pelo alvo mais específico
                        (vendedor › loja › unidade › empresa), depois pelo mais recente. Banner vale a partir da
                        data e hora marcadas; dica vale o dia cheio (00:00 às 23:59, horário de Brasília); frase de
                        comportamento não tem período e o app recebe todas as do comportamento.
                        {turmasNaRegua.length > 0 && (
                            <>
                                {' '}A faixa <strong>Turmas</strong> mostra o período de cada turma e as fases dentro
                                dela (hachurado = fase que fecha o placar), pelas janelas já materializadas — é o
                                mesmo histórico que decide a entrega de um comunicado de fase. Passe o mouse numa
                                barra, ou abra o formulário, para ver <strong>quais turmas recebem</strong>.
                            </>
                        )}
                    </p>
                </div>
            )}

            {hover && (
                <div
                    className="cm-tooltip"
                    style={{
                        left: Math.min(hover.x + 14, window.innerWidth - 340),
                        top: Math.min(hover.y + 14, window.innerHeight - 240),
                    }}
                >
                    <div className="cm-tt-head">
                        <span className="cm-tt-dot" style={{ background: SCOPE_COLOR[hover.item.scope] }} />
                        {TYPE_ICON[hover.item.type]} {hover.item.target_label}
                        <span className={`cm-status cm-status-${hover.item.status}`}>{hover.item.status}</span>
                    </div>
                    {hover.item.behavior_name && (
                        <div className="cm-tt-meta">Comportamento: <strong>{hover.item.behavior_name}</strong></div>
                    )}
                    {hover.item.title && <div className="cm-tt-title">{hover.item.title}</div>}
                    <div className="cm-tt-body"><BannerBody text={hover.item.body} /></div>
                    <div className="cm-tt-meta">
                        {hover.item.type === 'tip'
                            ? `${fmtDate(hover.item.starts_at)} → ${hover.item.ends_at ? fmtDate(hover.item.ends_at) : 'sem prazo'}`
                            : `${fmtDateTime(hover.item.starts_at)} → ${hover.item.ends_at ? fmtDateTime(hover.item.ends_at) : 'sem prazo'}`}
                    </div>
                    <div className="cm-tt-meta">
                        Prioridade {hover.item.priority} · 👁 {hover.item.read_count} · ✓ {hover.item.dismissed_count}
                        {' '}· 👍 {hover.item.like_count} · 👎 {hover.item.dislike_count}
                    </div>
                    {hover.item.author_name && <div className="cm-tt-meta">por {hover.item.author_name}</div>}
                    {hover.seg?.overlap && (
                        <div className="cm-tt-meta cm-tt-warn">
                            Disputa este intervalo com {hover.seg.eligible_ids.length - 1} outro(s) — este ganha.
                        </div>
                    )}
                    {hover.item.neverShown && (
                        <div className="cm-tt-meta cm-tt-warn">Coberto por outro comunicado: nunca aparece no período.</div>
                    )}
                </div>
            )}

            {/* --------------------------------------------------- formulário */}
            {formOpen && (
                <div className="cm-form-panel">
                    <h2>
                        {form.id ? `Editar ${TYPE_SINGULAR[form.type]} #${form.id}` : `Nov${form.type === 'banner' ? 'o' : 'a'} ${TYPE_SINGULAR[form.type]}`}
                    </h2>

                    <div className="cm-scope-row">
                        <label>Quem vê</label>
                        <div className="cm-scope-pills">
                            {/* A pílula "Fase" só existe se a empresa TEM fase. Sem esse filtro, toda
                                empresa sem programa — que no dia da subida é toda a base — mostra um
                                escopo que abre um select vazio e um botão desabilitado. */}
                            {SCOPE_ORDER.filter((s) => s !== 'phase' || fasesDaEmpresa.length > 0).map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    className={`cm-pill${form.scope === s ? ' active' : ''}`}
                                    style={form.scope === s ? { background: SCOPE_COLOR[s], borderColor: SCOPE_COLOR[s] } : undefined}
                                    onClick={() => setForm((p) => ({ ...p, scope: s }))}
                                >
                                    {SCOPE_LABEL[s]}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="cm-form-grid">
                        {form.type === 'behavior' && (
                            <div className="cm-field">
                                <label>Comportamento *</label>
                                <select value={form.behavior_key} onChange={(e) => setField('behavior_key', e.target.value)}>
                                    <option value="">— Selecione —</option>
                                    {behaviors.map((b) => (
                                        <option key={b.behavior_key} value={b.behavior_key}>{b.label}</option>
                                    ))}
                                </select>
                                <small>pode haver várias frases por comportamento</small>
                            </div>
                        )}
                        {form.scope === 'unit' && (
                            <div className="cm-field">
                                <label>Unidade *</label>
                                <select value={form.unit_id} onChange={(e) => setField('unit_id', e.target.value)}>
                                    <option value="">— Selecione —</option>
                                    {units.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
                                </select>
                            </div>
                        )}
                        {(form.scope === 'store' || form.scope === 'salesman') && (
                            <div className="cm-field">
                                <label>Loja *</label>
                                <select
                                    value={form.store_id}
                                    onChange={(e) => setForm((p) => ({ ...p, store_id: e.target.value, salesman_id: '' }))}
                                >
                                    <option value="">— Selecione —</option>
                                    {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                                </select>
                            </div>
                        )}
                        {form.scope === 'phase' && (
                            <div className="cm-field">
                                <label>Fase *</label>
                                <select value={form.phase_id} onChange={(e) => setField('phase_id', e.target.value)}>
                                    <option value="">— Selecione —</option>
                                    {fasesDaEmpresa.map((f) => (
                                        <option key={f.id} value={f.id}>{f.label}</option>
                                    ))}
                                </select>
                                <small>
                                    vale enquanto o vendedor estiver nesta fase, e ganha dos comunicados
                                    de empresa, unidade, loja e vendedor
                                </small>
                            </div>
                        )}
                        {form.scope === 'salesman' && (
                            <div className="cm-field">
                                <label>Vendedor *</label>
                                <select value={form.salesman_id} onChange={(e) => setField('salesman_id', e.target.value)} disabled={!form.store_id}>
                                    <option value="">— Selecione —</option>
                                    {formSalesmen.map((s) => (
                                        <option key={s.salesman_id} value={s.salesman_id}>
                                            {s.salesman_name}{s.is_active ? '' : ' (inativo)'}{faseTexto(fasesVend[String(s.salesman_id)], { comDia: false })}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="cm-field">
                            <label>{form.type === 'push' ? `Título * (${form.title.length}/${PUSH_MAX_TITLE})` : 'Título (opcional)'}</label>
                            <input
                                type="text" maxLength={form.type === 'push' ? PUSH_MAX_TITLE : 120} value={form.title}
                                onChange={(e) => setField('title', e.target.value)}
                                placeholder={form.type === 'tip' ? 'Ex.: Dica do dia'
                                    : form.type === 'push' ? 'Ex.: <first_name>, sentimos sua falta!'
                                        : 'Ex.: Reunião de equipe'}
                            />
                            {form.type === 'push' && <small>é a linha em negrito da notificação</small>}
                        </div>
                        {/* Ícone, tom, prioridade e rótulo do botão são da tela do app. A notificação
                            do celular tem só título e texto, então no push esses campos somem. */}
                        {form.type !== 'push' && (<>
                        <div className="cm-field cm-field-emoji">
                            <label>Ícone (emoji)</label>
                            <input
                                type="text" maxLength={8} value={form.icon}
                                onChange={(e) => setField('icon', e.target.value)}
                                placeholder="💡"
                            />
                            <small>
                                {form.type === 'tip'
                                    ? 'o app usa como título visual da dica'
                                    : 'aparece junto do texto no app'}
                                {' '}— campo próprio, para não virar parte do corpo
                            </small>
                            <div className="cm-emoji-atalhos">
                                {['💡', '🎯', '⭐', '🔥', '📣', '🤝', '👏', '🧠'].map((e) => (
                                    <button key={e} type="button" className="cm-emoji-atalho"
                                        onClick={() => setField('icon', e)}>{e}</button>
                                ))}
                            </div>
                        </div>
                        {form.type === 'behavior' && (
                            <div className="cm-field">
                                <label>Autor da frase (opcional)</label>
                                <input
                                    type="text" maxLength={120} value={form.author}
                                    onChange={(e) => setField('author', e.target.value)}
                                    placeholder="Ex.: Mahatma Gandhi, Autor desconhecido"
                                />
                                <small>quem disse a frase — não é quem cadastrou o comunicado</small>
                            </div>
                        )}
                        <div className="cm-field">
                            <label>Tom</label>
                            <select value={form.severity} onChange={(e) => setField('severity', e.target.value)}>
                                {Object.entries(SEVERITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </div>
                        </>)}

                        {form.type === 'banner' && (
                            <>
                                <div className="cm-field">
                                    <label>Início * (data e hora)</label>
                                    <input type="datetime-local" value={form.starts_at} onChange={(e) => setField('starts_at', e.target.value)} />
                                </div>
                                <div className="cm-field">
                                    <label>Fim (vazio = sem prazo)</label>
                                    <input type="datetime-local" value={form.ends_at} onChange={(e) => setField('ends_at', e.target.value)} />
                                </div>
                            </>
                        )}
                        {form.type === 'tip' && (
                            <>
                                <div className="cm-field">
                                    <label>Primeiro dia *</label>
                                    <input type="date" value={form.starts_at} onChange={(e) => setField('starts_at', e.target.value)} />
                                </div>
                                <div className="cm-field">
                                    <label>Último dia (vazio = sem prazo)</label>
                                    <input type="date" value={form.ends_at} onChange={(e) => setField('ends_at', e.target.value)} />
                                    <small>dia cheio, 00:00 às 23:59 (BRT)</small>
                                </div>
                                <div className="cm-field">
                                    <label>&nbsp;</label>
                                    <button type="button" className="cm-btn" onClick={setTipToday}>Só hoje</button>
                                </div>
                            </>
                        )}

                        {/* Prioridade é o desempate da FILA (banner/dica). Mudança de fase não tem
                            fila: é um ativo por fase, então o campo não teria efeito — some. */}
                        {form.type !== 'phase_change' && form.type !== 'push' && (
                            <div className="cm-field">
                                <label>Prioridade (0–100)</label>
                                <input
                                    type="number" min={0} max={100} step={1} value={form.priority}
                                    onChange={(e) => setField('priority', e.target.value)}
                                />
                                <small>0 = o alvo mais específico ganha. Maior fura a fila.</small>
                            </div>
                        )}
                        {form.type !== 'push' && (
                            <div className="cm-field">
                                <label>Rótulo do botão</label>
                                <input
                                    type="text" maxLength={40} value={form.cta_label}
                                    onChange={(e) => setField('cta_label', e.target.value)}
                                    placeholder="Entendi"
                                />
                            </div>
                        )}
                    </div>

                    {/* §N31 — push: QUANDO checar e SE envia. Separados de propósito: a regra
                        ("2 dias sem gravar") vira verdade a qualquer hora, mas só é consultada nos
                        horários da agenda, para o push não chegar no fim do expediente. */}
                    {form.type === 'push' && (
                        <div className="cm-push">
                            <div className="cm-push-bloco">
                                <h3>Quando</h3>
                                <div className="cm-scope-pills">
                                    {[['once', 'Envio único'], ['recurring', 'Recorrente']].map(([m, lbl]) => (
                                        <button key={m} type="button"
                                            className={`cm-pill${form.push_mode === m ? ' active' : ''}`}
                                            onClick={() => setField('push_mode', m)}>{lbl}</button>
                                    ))}
                                </div>
                                {form.push_mode === 'once' ? (
                                    <div className="cm-form-grid">
                                        <div className="cm-field">
                                            <label>Enviar em (vazio = assim que publicar)</label>
                                            <input type="datetime-local" value={form.starts_at}
                                                onChange={(e) => setField('starts_at', e.target.value)} />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="cm-form-grid">
                                        <div className="cm-field">
                                            <label>Horários (BRT) *</label>
                                            <div className="cm-push-horarios">
                                                {form.push_times.map((t, i) => (
                                                    <span key={i} className="cm-push-horario">
                                                        <Horario24 value={t}
                                                            onChange={(v) => setField('push_times',
                                                                form.push_times.map((x, j) => (j === i ? v : x)))} />
                                                        {form.push_times.length > 1 && (
                                                            <button type="button" className="cm-btn cm-btn-sm" title="Remover horário"
                                                                onClick={() => setField('push_times', form.push_times.filter((_, j) => j !== i))}>✕</button>
                                                        )}
                                                    </span>
                                                ))}
                                                {form.push_times.length < 12 && (
                                                    <button type="button" className="cm-btn cm-btn-sm"
                                                        onClick={() => setField('push_times', [...form.push_times, '16:00'])}>+ horário</button>
                                                )}
                                            </div>
                                            <small>em cada horário a condição é checada uma vez, e o push vai para quem passar</small>
                                        </div>
                                        <div className="cm-field">
                                            <label>Dias</label>
                                            <div className="cm-push-dias">
                                                {WEEKDAYS.map(([d, lbl]) => (
                                                    <label key={d} className="cm-check">
                                                        <input type="checkbox" checked={form.push_weekdays.includes(d)}
                                                            onChange={(e) => setField('push_weekdays', e.target.checked
                                                                ? [...form.push_weekdays, d].sort()
                                                                : form.push_weekdays.filter((x) => x !== d))} />
                                                        {lbl}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="cm-field">
                                            <label>Vale a partir de (vazio = já)</label>
                                            <input type="datetime-local" value={form.starts_at}
                                                onChange={(e) => setField('starts_at', e.target.value)} />
                                        </div>
                                        <div className="cm-field">
                                            <label>Até (vazio = sem prazo)</label>
                                            <input type="datetime-local" value={form.ends_at}
                                                onChange={(e) => setField('ends_at', e.target.value)} />
                                        </div>
                                        <div className="cm-field">
                                            <label>Atraso máximo (min)</label>
                                            <input type="number" min={5} max={240} step={5} value={form.push_tolerance}
                                                onChange={(e) => setField('push_tolerance', e.target.value)} />
                                            <small>horário que passar disto sem ser enviado é pulado, não enviado atrasado</small>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="cm-push-bloco">
                                <h3>Condição</h3>
                                <div className="cm-form-grid">
                                    <div className="cm-field">
                                        <label>Quem do escopo recebe</label>
                                        <select value={form.rule_kind}
                                            onChange={(e) => setForm((p) => ({ ...p, rule_kind: e.target.value, rule_params: {} }))}>
                                            <option value="">Todos</option>
                                            {pushRules.map((r) => <option key={r.kind} value={r.kind}>{r.label}</option>)}
                                        </select>
                                        {form.rule_kind && (
                                            <small>{pushRules.find((r) => r.kind === form.rule_kind)?.description}</small>
                                        )}
                                    </div>
                                    {(pushRules.find((r) => r.kind === form.rule_kind)?.params || []).map((p) => (
                                        <div className="cm-field" key={p.name}>
                                            <label>{p.label}{p.required ? ' *' : ''}</label>
                                            <input type="number" min={p.min} max={p.max} step={1}
                                                value={form.rule_params[p.name] ?? p.default ?? ''}
                                                onChange={(e) => setField('rule_params', { ...form.rule_params, [p.name]: e.target.value })} />
                                        </div>
                                    ))}
                                    {form.rule_kind && (
                                        <div className="cm-field">
                                            <label>Repetir para a mesma pessoa</label>
                                            <select value={form.rule_resend} onChange={(e) => setField('rule_resend', e.target.value)}>
                                                {Object.entries(RESEND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                            </select>
                                            {form.rule_resend === 'once_per_occurrence' && (
                                                <small>{pushRules.find((r) => r.kind === form.rule_kind)?.occurrence}</small>
                                            )}
                                        </div>
                                    )}
                                    {form.rule_kind && form.rule_resend === 'min_hours' && (
                                        <div className="cm-field">
                                            <label>Intervalo mínimo (horas)</label>
                                            <input type="number" min={1} max={720} step={1} value={form.rule_min_hours}
                                                onChange={(e) => setField('rule_min_hours', e.target.value)} />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="cm-push-bloco">
                                <h3>Entrega</h3>
                                <div className="cm-form-grid">
                                    <div className="cm-field">
                                        <label>Substituir o aviso anterior (chave)</label>
                                        <input type="text" maxLength={40} placeholder="vazio = não substitui"
                                            value={form.push_collapse_key}
                                            onChange={(e) => setField('push_collapse_key',
                                                e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))} />
                                        <small>pushes com a mesma chave não se acumulam: o novo troca o antigo na bandeja, e um aparelho
                                            desligado recebe só o último. Letras minúsculas, números, _ e -.</small>
                                    </div>
                                    {form.push_collapse_key.trim() && (
                                        <div className="cm-field">
                                            <label>Validade no aparelho offline (min)</label>
                                            <input type="number" min={1} max={1440} step={1} value={form.push_ttl}
                                                onChange={(e) => setField('push_ttl', e.target.value)} />
                                            <small>depois disto o push que não foi entregue é descartado (1 a 1440)</small>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <p className="cm-push-resumo">
                                {resumoPush({
                                    ...pushPayload(form),
                                    schedule: pushPayload(form).delivery_schedule,
                                    rule: pushPayload(form).trigger_rule,
                                    starts_at: form.starts_at,
                                    rules: pushRules,
                                })}
                            </p>
                        </div>
                    )}

                    {/* QUEM VAI RECEBER, em turmas (§9.13). Fica junto das datas porque é a conferência
                        que o CS faz antes de salvar: escopo + janela = audiência. */}
                    {turmasNaRegua.length > 0 && form.type !== 'push' && (
                        <div className={`cm-audiencia${audiencia.length ? '' : ' cm-audiencia-vazia'}`}>
                            <div className="cm-audiencia-head">
                                <strong>
                                    {audiencia.length
                                        ? `${audiencia.length} ${audiencia.length === 1 ? 'turma vai receber' : 'turmas vão receber'}`
                                        : 'Nenhuma turma recebe nesta janela'}
                                </strong>
                                {form.scope === 'company' && (
                                    <span> — escopo empresa: alcança também quem <strong>não</strong> está em turma</span>
                                )}
                                {form.type === 'behavior' && <span> — frase não tem período; vale sempre</span>}
                            </div>

                            {audiencia.length > 0 && (
                                <ul className="cm-audiencia-lista">
                                    {audiencia.map((c) => (
                                        <li key={c.cohort_id}>
                                            <span className="cm-audiencia-nome">{c.name}</span>
                                            <span className="cm-audiencia-loja">{c.store_name || '—'}</span>
                                            <span className="cm-audiencia-dias">
                                                {form.scope === 'phase' ? `${c.faixa.name}: ` : ''}
                                                {fmtDia(c.faixa.date_start)} → {c.faixa.date_end ? fmtDia(c.faixa.date_end) : 'sem fim'}
                                            </span>
                                            <span className="cm-audiencia-n">
                                                {form.scope === 'phase' ? c.faixa.members : c.members}
                                                {(form.scope === 'phase' ? c.faixa.members : c.members) === 1 ? ' membro' : ' membros'}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {/* O erro que este bloco existe para pegar: janela do comunicado fora da janela
                                da fase. Salva sem reclamar e nunca aparece para ninguém. */}
                            {faseNasTurmas.length > 0 && audiencia.length === 0 && (
                                <p className="cm-audiencia-alerta">
                                    ⚠ Esta fase acontece em:{' '}
                                    {faseNasTurmas.map((f, i) => (
                                        <span key={`${f.cohort}-${i}`}>
                                            {i > 0 ? ' · ' : ''}
                                            {f.cohort} ({fmtDia(f.date_start)} → {f.date_end ? fmtDia(f.date_end) : 'sem fim'})
                                        </span>
                                    ))}
                                    . A janela escolhida não cruza nenhuma — ninguém receberia.
                                </p>
                            )}
                            {form.scope === 'phase' && !form.phase_id && (
                                <p className="cm-audiencia-nota">Escolha a fase para ver as turmas e as datas.</p>
                            )}
                        </div>
                    )}

                    {/* editor de texto */}
                    <div className="cm-editor">
                        <div className="cm-editor-col">
                            <label>Texto *</label>
                            {form.type === 'push' ? (
                                <div className="cm-toolbar">
                                    <span className="cm-toolbar-hint">texto puro — a notificação não tem negrito nem itálico</span>
                                    <span className="cm-counter">{form.body.length}/{PUSH_MAX_BODY}</span>
                                </div>
                            ) : (
                            <div className="cm-toolbar">
                                <button type="button" className="cm-tool" onClick={() => applyMark('**')} title="Negrito"><strong>B</strong></button>
                                <button type="button" className="cm-tool" onClick={() => applyMark('_')} title="Itálico"><em>I</em></button>
                                <button type="button" className="cm-tool" onClick={() => insertText('\n')} title="Quebra de linha">↵</button>
                                <div className="cm-emoji-wrap">
                                    <button type="button" className="cm-tool" onClick={() => setEmojiOpen((v) => !v)} title="Emoji">😀</button>
                                    {emojiOpen && (
                                        <div className="cm-emoji-pop">
                                            {EMOJIS.map((em) => (
                                                <button key={em} type="button" onClick={() => { insertText(em); setEmojiOpen(false); }}>{em}</button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <span className="cm-toolbar-hint">**negrito** · _itálico_ · emoji do teclado também funciona</span>
                                <span className="cm-counter">{form.body.length}/{MAX_BODY}</span>
                            </div>
                            )}
                            <textarea
                                ref={bodyRef}
                                className="cm-body"
                                rows={7}
                                maxLength={form.type === 'push' ? PUSH_MAX_BODY : MAX_BODY}
                                value={form.body}
                                onChange={(e) => setField('body', e.target.value)}
                                placeholder={form.type === 'behavior'
                                    ? 'Ex.: Pergunte **para quem** é o produto antes de sugerir.'
                                    : form.type === 'phase_change'
                                        ? 'Ex.: Você avançou para *<phase_name>*! 🎉'
                                        : form.type === 'push'
                                            ? 'Ex.: Faz 2 dias que você não grava. Bora voltar hoje? 🎙️'
                                            : 'Ex.: 🎉 **Parabéns pela semana!** Reunião _hoje_ às 18h.'}
                            />
                            {form.type === 'push' && (
                                <div className="cm-tags">
                                    <strong>Tags trocadas pelos dados de cada vendedor</strong>, no título e no texto.
                                    Clique para inserir no texto. Para um valor padrão quando não houver dado,
                                    escreva depois de <code>|</code>: <code>{'<store_name|sua loja>'}</code>.
                                    Sem padrão, a <strong>linha</strong> com a tag some (no título, só a tag).
                                    {Object.entries(pushTags.reduce((g, t) => {
                                        (g[t.group] = g[t.group] || []).push(t); return g;
                                    }, {})).map(([grupo, lista]) => (
                                        <div key={grupo} className="cm-tags-grupo">
                                            <small>{PUSH_TAG_GROUP_LABEL[grupo] || grupo}</small>
                                            <ul>
                                                {lista.map((t) => (
                                                    <li key={t.tag}>
                                                        <button type="button" className="cm-tag-btn"
                                                            onClick={() => insertText(t.tag)}>{t.tag}</button>
                                                        <span>{t.label} — ex.: {t.example}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {form.type === 'phase_change' && form.scope !== 'phase' && (
                                <div className="cm-tags cm-tags-alerta">
                                    <strong>Escolha uma fase no seletor de escopo acima.</strong> Este
                                    comunicado é sempre de uma fase — é ela que decide quando ele
                                    aparece para o vendedor.
                                </div>
                            )}
                            {form.type === 'phase_change' && (
                                <div className="cm-tags">
                                    <strong>O servidor troca estas tags pelos números do vendedor</strong>,
                                    calculados sobre a fase que ele <em>acabou de concluir</em>.
                                    Clique para inserir. A linha que tiver uma tag sem valor
                                    — comportamento numa fase de nivelamento, por exemplo —
                                    <strong> some inteira</strong>, então o mesmo texto serve para qualquer fase.
                                    <ul>
                                        {TAGS_DINAMICAS.map(([tag, oq]) => (
                                            <li key={tag}>
                                                <button type="button" className="cm-tag-btn"
                                                    onClick={() => insertText(tag)}>{tag}</button>
                                                <span>{oq}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                        <div className="cm-editor-col">
                            <label>{form.type === 'push' ? 'Prévia da notificação' : 'Prévia do texto formatado'}</label>
                            {form.type === 'push' ? (
                                <div className="cm-preview cm-push-notif">
                                    <div className="cm-push-notif-app">🔔 Aliado · agora</div>
                                    <div className="cm-preview-title">
                                        {previewTags(form.title || 'Título da notificação', pushTags)}
                                    </div>
                                    <div className="cm-preview-body">
                                        {form.body.trim()
                                            ? previewTags(form.body, pushTags)
                                            : <span className="cm-empty">o texto aparece aqui…</span>}
                                    </div>
                                </div>
                            ) : (
                            <div className="cm-preview">
                                {form.title && <div className="cm-preview-title">{form.title}</div>}
                                <div className="cm-preview-body">
                                    {form.body.trim()
                                        ? <BannerBody text={form.body} />
                                        : <span className="cm-empty">o texto aparece aqui…</span>}
                                </div>
                            </div>
                            )}
                            <small className="cm-preview-hint">
                                Só confere a formatação (negrito, itálico, emoji e quebras de linha) — o layout no app
                                é responsabilidade do app e varia por tela.
                            </small>
                        </div>
                    </div>

                    <div className="cm-form-flags">
                        {/* "Publicado" sai da mudança de fase: é UM ativo por fase, então salvar
                            desmarcado só produziria um texto que nunca aparece — e desativar o que
                            está no ar continua sendo o botão da lista, que é onde a ação faz
                            sentido (desativa um, cria o próximo). */}
                        {form.type !== 'phase_change' && (
                            <label className="cm-check">
                                <input type="checkbox" checked={form.active} onChange={(e) => setField('active', e.target.checked)} />
                                Publicado (desmarque para guardar sem exibir)
                            </label>
                        )}
                        {form.id && (
                            <label className="cm-check">
                                <input type="checkbox" checked={form.bump_version} onChange={(e) => setField('bump_version', e.target.checked)} />
                                Reexibir para quem já confirmou
                            </label>
                        )}
                    </div>

                    <div className="cm-form-actions">
                        <button className="cm-btn" onClick={closeForm} disabled={saving}>Cancelar</button>
                        <button className="cm-btn cm-btn-primary" onClick={save} disabled={saving || !canSave}>
                            {saving ? 'Salvando…' : form.id ? 'Salvar alterações' : 'Publicar'}
                        </button>
                    </div>
                </div>
            )}

            {/* ------------------------------------------- lista com janela */}
            {subdomain && withWindow && (
                <div className="cm-list">
                    <h2>{TYPE_LABEL[activeType]} {loading ? '(carregando…)' : `(${items.length})`}</h2>
                    {!loading && items.length === 0 && <p className="cm-empty">Nada cadastrado neste tipo.</p>}
                    {items.length > 0 && (
                        <table className="cm-table">
                            <thead>
                                <tr>
                                    <th>Quem vê</th><th>Texto</th><th>{activeType === 'tip' ? 'Dias' : 'Janela'}</th>
                                    <th>Prior.</th><th>Status</th><th>Interações</th><th>Autor</th><th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((b) => (
                                    <React.Fragment key={b.id}>
                                        <tr className={timeline?.never_shown?.includes(b.id) ? 'cm-row-covered' : undefined}>
                                            <td>
                                                <span className="cm-tt-dot" style={{ background: SCOPE_COLOR[b.scope] }} />
                                                {b.target_label}
                                            </td>
                                            <td>
                                                {b.icon && <span className="cm-icone">{b.icon}</span>}
                                                {b.title && <strong>{b.title}<br /></strong>}
                                                {truncate(b.body, 80)}
                                                {timeline?.never_shown?.includes(b.id) &&
                                                    <><br /><small className="cm-warn">⚠ coberto por outro no período da régua</small></>}
                                            </td>
                                            <td>
                                                {b.type === 'tip' ? fmtDate(b.starts_at) : fmtDateTime(b.starts_at)}<br />
                                                <small>{b.ends_at
                                                    ? `até ${b.type === 'tip' ? fmtDate(b.ends_at) : fmtDateTime(b.ends_at)}`
                                                    : 'sem prazo'}</small>
                                            </td>
                                            <td>{b.priority}</td>
                                            <td><span className={`cm-status cm-status-${b.status}`}>{b.status}</span></td>
                                            <td>{interactionChips(b)}</td>
                                            <td>
                                                {b.author_name || '—'}
                                                {b.editor_name && <><br /><small>editou: {b.editor_name}</small></>}
                                            </td>
                                            <td className="cm-actions">
                                                <button className="cm-btn cm-btn-sm" onClick={() => openEdit(b)}>✏️ Editar</button>
                                                <button className="cm-btn cm-btn-sm" onClick={() => toggleActive(b)}>
                                                    {b.active ? '⏸ Desativar' : '▶ Ativar'}
                                                </button>
                                                <button className="cm-btn cm-btn-sm" onClick={() => duplicate(b)}>⧉ Duplicar</button>
                                                <button className="cm-btn cm-btn-sm cm-btn-danger" onClick={() => remove(b)}>🗑️ Remover</button>
                                            </td>
                                        </tr>
                                        {detailRow(b, 8)}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* ------------------------------- mudança de fase (§N27): sem janela e sem fila.
                A lista é por FASE, e não tem coluna de prioridade nem de período — os dois não
                existem aqui. Antes este tipo caía no bloco de frases só porque o critério de
                renderização era "tem janela?", e o resultado era a aba abrir mostrando a tabela
                de comportamentos. */}
            {subdomain && activeType === 'phase_change' && (
                <div className="cm-list">
                    <h2>{TYPE_LABEL[activeType]} {loading ? '(carregando…)' : `(${items.length})`}</h2>
                    <p className="cm-regua-foot">
                        <strong>Um por fase, e sempre existe</strong> — não se desativa nem se
                        remove; para mudar o texto, edite. O vendedor vê quando entra na fase, e
                        some quando ele confirma. Sem período (a janela é a própria fase) e sem
                        prioridade (não há fila). As <code>&lt;tags&gt;</code> são trocadas pelos
                        números da fase que ele <em>acabou de concluir</em>.
                        <br />
                        A lista está na <strong>ordem da jornada</strong>: a fase que acontece
                        primeiro aparece primeiro.
                    </p>
                    {!loading && items.length === 0 && (
                        <p className="cm-empty">
                            Nenhum comunicado de mudança de fase. Toda fase criada nasce com um; se
                            esta empresa tem fases antigas sem ele, use o seed do plano (§N27).
                        </p>
                    )}
                    {items.length > 0 && (
                        <table className="cm-table">
                            <thead>
                                <tr>
                                    <th>Fase</th><th>Texto</th><th>Status</th>
                                    <th>Interações</th><th>Autor</th><th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((b) => (
                                    <React.Fragment key={b.id}>
                                        <tr>
                                            <td>
                                                <span className="cm-tt-dot" style={{ background: SCOPE_COLOR[b.scope] }} />
                                                {b.phase_name
                                                    ? <>{b.phase_sort_order}. {b.phase_name}
                                                        {b.program_name && <><br /><small>{b.program_name}</small></>}</>
                                                    : b.target_label}
                                            </td>
                                            <td>
                                                {b.icon && <span className="cm-icone">{b.icon}</span>}
                                                {b.title && <strong>{b.title}<br /></strong>}
                                                {truncate(b.body, 90)}
                                            </td>
                                            <td><span className={`cm-status cm-status-${b.status}`}>{b.status}</span></td>
                                            <td>{interactionChips(b)}</td>
                                            <td>
                                                {b.author_name || '—'}
                                                {b.editor_name && <><br /><small>editou: {b.editor_name}</small></>}
                                            </td>
                                            {/* Sem Desativar e sem Remover: toda fase tem o seu,
                                                sempre. Trocar o texto é Editar. */}
                                            <td className="cm-actions">
                                                <button className="cm-btn cm-btn-sm" onClick={() => openEdit(b)}>✏️ Editar</button>
                                            </td>
                                        </tr>
                                        {detailRow(b, 6)}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* --------------------------- repertório de frases (sem janela) */}
            {/* ------------------------------------------ push (§N31): enviado pelo servidor.
                Sem régua e sem interações de tela: o que se acompanha é QUANDO sai (próximo
                horário), para QUEM (prévia com o motivo) e o que saiu (histórico por horário). */}
            {subdomain && activeType === 'push' && (
                <div className="cm-list">
                    <h2>{TYPE_LABEL[activeType]} {loading ? '(carregando…)' : `(${items.length})`}</h2>
                    <p className="cm-regua-foot">
                        O servidor envia a notificação ao celular nos horários da agenda, checando a condição
                        em cada horário. Chega mesmo com o app fechado. Só Android.
                    </p>
                    {!loading && items.length === 0 && <p className="cm-empty">Nenhum push cadastrado.</p>}
                    {items.length > 0 && (
                        <table className="cm-table">
                            <thead>
                                <tr>
                                    <th>Quem vê</th><th>Notificação</th><th>Quando e para quem</th>
                                    <th>Envios</th><th>Status</th><th>Autor</th><th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((b) => (
                                    <React.Fragment key={b.id}>
                                        <tr>
                                            <td>
                                                <span className="cm-tt-dot" style={{ background: SCOPE_COLOR[b.scope] }} />
                                                {b.target_label}
                                                <br /><small>{b.audience_count ?? '—'} no escopo</small>
                                            </td>
                                            <td>
                                                <strong>{b.title}</strong><br />
                                                {truncate(b.body, 80)}
                                            </td>
                                            <td>
                                                {resumoPush({ schedule: b.delivery_schedule, rule: b.trigger_rule,
                                                    starts_at: b.starts_at, rules: pushRules })}
                                                {b.ends_at && <><br /><small>até {fmtDateTime(b.ends_at)}</small></>}
                                            </td>
                                            <td>
                                                <span title="enviados">📨 {b.sent_count ?? 0}</span>
                                                {(b.no_token_count > 0) && <span title="sem aparelho cadastrado"> · 📵 {b.no_token_count}</span>}
                                                {(b.failed_count > 0) && <span className="cm-warn" title="falharam"> · ⚠ {b.failed_count}</span>}
                                                <br /><small>
                                                    {b.next_slot_at ? `próximo: ${fmtDateTime(b.next_slot_at)}`
                                                        : b.active ? 'sem próximo horário' : 'desativado'}
                                                </small>
                                            </td>
                                            <td><span className={`cm-status cm-status-${b.active ? 'ativo' : 'inativo'}`}>
                                                {b.active ? 'ativo' : 'inativo'}</span></td>
                                            <td>
                                                {b.author_name || '—'}
                                                {b.editor_name && <><br /><small>editou: {b.editor_name}</small></>}
                                            </td>
                                            <td className="cm-actions">
                                                <button className="cm-btn cm-btn-sm" onClick={() => showPush(b, 'preview')}>👥 Quem receberia</button>
                                                <button className="cm-btn cm-btn-sm" onClick={() => showPush(b, 'deliveries')}>🧾 Histórico</button>
                                                <button className="cm-btn cm-btn-sm" onClick={() => openEdit(b)}>✏️ Editar</button>
                                                <button className="cm-btn cm-btn-sm" onClick={() => toggleActive(b)}>
                                                    {b.active ? '⏸ Desativar' : '▶ Ativar'}
                                                </button>
                                                <button className="cm-btn cm-btn-sm" onClick={() => duplicate(b)}>⧉ Duplicar</button>
                                                <button className="cm-btn cm-btn-sm cm-btn-danger" onClick={() => remove(b)}>🗑️ Remover</button>
                                            </td>
                                        </tr>
                                        {pushInfo?.id === b.id && (
                                            <tr className="cm-detail-row">
                                                <td colSpan={7}>
                                                    <div className="cm-readers">
                                                        <div className="cm-readers-head">
                                                            <strong>
                                                                {pushInfo.kind === 'preview'
                                                                    ? `Se a condição fosse checada agora: ${pushInfo.data.would_send} de ${pushInfo.data.audience} receberiam`
                                                                    : 'Histórico por horário'}
                                                            </strong>
                                                            <button className="cm-btn cm-btn-sm" onClick={() => setPushInfo(null)}>Fechar</button>
                                                        </div>
                                                        {pushInfo.kind === 'preview' && (
                                                            <>
                                                                <p className="cm-regua-foot">
                                                                    Próximos horários: {pushInfo.data.next_slots.length
                                                                        ? pushInfo.data.next_slots.map(fmtDateTime).join(' · ') : '—'}
                                                                </p>
                                                                {pushInfo.data.recipients.length === 0
                                                                    ? <p className="cm-empty">Ninguém passa na condição agora.</p>
                                                                    : (
                                                                        <table className="cm-table cm-table-inner">
                                                                            <thead><tr><th>Vendedor</th><th>Motivo</th><th>Aparelho</th><th>Já recebeu</th><th>Notificação</th></tr></thead>
                                                                            <tbody>
                                                                                {pushInfo.data.recipients.map((r) => (
                                                                                    <tr key={r.salesman_id}>
                                                                                        <td>{r.fullname || `vendedor ${r.salesman_id}`}</td>
                                                                                        <td>{r.reason}</td>
                                                                                        <td>{r.has_device ? '📱' : '📵 sem aparelho'}</td>
                                                                                        <td>{r.already_sent ? 'sim (não repete)' : '—'}</td>
                                                                                        <td><strong>{r.title}</strong>{r.body && <><br /><small className="cm-pre">{r.body}</small></>}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    )}
                                                            </>
                                                        )}
                                                        {pushInfo.kind === 'deliveries' && (
                                                            pushInfo.data.slots.length === 0
                                                                ? <p className="cm-empty">Nenhum horário processado ainda.</p>
                                                                : pushInfo.data.slots.map((sl) => (
                                                                    <details key={sl.id} className="cm-push-slot">
                                                                        <summary>
                                                                            {fmtDateTime(sl.slot_at)} — {sl.status === 'missed'
                                                                                ? 'pulado (passou do atraso máximo)'
                                                                                : sl.status === 'failed' ? `falhou: ${sl.error || ''}`
                                                                                    : `${sl.sent} enviados · ${sl.no_token} sem aparelho · ${sl.failed} falhas · ${sl.skipped} já tinham recebido`}
                                                                            {sl.communication_version > 1 && <small> (v{sl.communication_version})</small>}
                                                                        </summary>
                                                                        {sl.deliveries.length > 0 && (
                                                                            <table className="cm-table cm-table-inner">
                                                                                <thead><tr><th>Vendedor</th><th>Status</th><th>Motivo</th><th>Enviado</th><th>Texto recebido</th></tr></thead>
                                                                                <tbody>
                                                                                    {sl.deliveries.map((d, i) => (
                                                                                        <tr key={i}>
                                                                                            <td>{d.fullname || `vendedor ${d.salesman_id}`}</td>
                                                                                            <td>{{ sent: '📨 enviado', no_token: '📵 sem aparelho', failed: '⚠ falhou', sending: 'enviando…' }[d.status] || d.status}
                                                                                                {d.error && <><br /><small>{d.error}</small></>}</td>
                                                                                            <td>{d.reason || '—'}</td>
                                                                                            <td>{fmtDateTime(d.sent_at)}</td>
                                                                                            <td>{d.rendered_title
                                                                                                ? <><strong>{d.rendered_title}</strong><br /><small className="cm-pre">{d.rendered_body}</small></>
                                                                                                : '—'}</td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        )}
                                                                    </details>
                                                                ))
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {subdomain && activeType === 'behavior' && (
                <div className="cm-list">
                    <h2>Frases por comportamento {loading ? '(carregando…)' : `(${items.length})`}</h2>
                    <p className="cm-regua-foot">
                        Frases não têm período: valem sempre, e o app recebe <strong>todas</strong> as do
                        comportamento (não é fila). Prioridade só ordena o repertório.
                    </p>
                    {!loading && items.length === 0 && <p className="cm-empty">Nenhuma frase cadastrada.</p>}
                    {grouped?.map(([behavior, rows]) => (
                        <div key={behavior} className="cm-group">
                            <h3 className="cm-group-title">🗣️ {behavior} <small>({rows.length})</small></h3>
                            <table className="cm-table">
                                <thead>
                                    <tr>
                                        <th>Comportamento</th><th>Quem vê</th><th>Texto</th><th>Prior.</th>
                                        <th>Status</th><th>Interações</th><th>Autor</th><th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((b) => (
                                        <React.Fragment key={b.id}>
                                            <tr>
                                                <td>
                                                    <strong>{b.behavior_name || b.behavior_key || '—'}</strong>
                                                    {b.behavior_name && b.behavior_key &&
                                                        <><br /><small>{b.behavior_key}</small></>}
                                                </td>
                                                <td>
                                                    <span className="cm-tt-dot" style={{ background: SCOPE_COLOR[b.scope] }} />
                                                    {b.target_label}
                                                </td>
                                                <td>
                                                    {b.icon && <span className="cm-icone">{b.icon}</span>}
                                                    {b.title && <strong>{b.title}<br /></strong>}
                                                    {truncate(b.body, 90)}
                                                    {/* autor DA FRASE; a coluna "Autor" da tabela é
                                                        quem cadastrou o comunicado — nomes parecidos,
                                                        significados diferentes */}
                                                    {b.author && <><br /><small className="cm-autor-frase">— {b.author}</small></>}
                                                </td>
                                                <td>{b.priority}</td>
                                                <td><span className={`cm-status cm-status-${b.status}`}>{b.status}</span></td>
                                                <td>{interactionChips(b)}</td>
                                                <td>{b.author_name || '—'}</td>
                                                <td className="cm-actions">
                                                    <button className="cm-btn cm-btn-sm" onClick={() => openEdit(b)}>✏️ Editar</button>
                                                    <button className="cm-btn cm-btn-sm" onClick={() => toggleActive(b)}>
                                                        {b.active ? '⏸ Desativar' : '▶ Ativar'}
                                                    </button>
                                                    <button className="cm-btn cm-btn-sm" onClick={() => duplicate(b)}>⧉ Duplicar</button>
                                                    <button className="cm-btn cm-btn-sm cm-btn-danger" onClick={() => remove(b)}>🗑️ Remover</button>
                                                </td>
                                            </tr>
                                            {detailRow(b, 8)}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default Comunicados;
